import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { ClipboardCheck, Scale, SlidersHorizontal, TrendingDown } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge, type BadgeProps } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataTable, arrIncludesSome, type FacetedFilter } from '@/components/ui/DataTable';
import { MetricCard } from '@/components/financials/MetricCard';
import { AdjustmentDialog } from '@/components/inventory/AdjustmentDialog';
import { EditStockMovementDialog } from '@/components/inventory/EditStockMovementDialog';
import {
  ADJUSTMENT_REASONS,
  ADJUSTMENT_REASON_LABELS,
  STOCK_MOVEMENT_TYPE_LABELS,
  useStockMovements,
  type AdjustmentReason,
  type StockMovementRow,
} from '@/features/inventory/hooks';
import { hasCycleCountInProgress } from '@/features/inventory/cycleCount';
import { useAuth } from '@/app/AuthProvider';
import { canWrite } from '@/lib/permissions';
import { formatDateTime } from '@/lib/formatters';

const REASON_OPTIONS = ADJUSTMENT_REASONS.map((r) => ({
  value: r,
  label: ADJUSTMENT_REASON_LABELS[r],
}));

const MODE_OPTIONS = [
  { value: 'cycle_count', label: STOCK_MOVEMENT_TYPE_LABELS.cycle_count },
  { value: 'adjustment', label: STOCK_MOVEMENT_TYPE_LABELS.adjustment },
];

const MODE_VARIANT: Record<string, NonNullable<BadgeProps['variant']>> = {
  cycle_count: 'info',
  adjustment: 'secondary',
};

function reasonLabel(code: string | null): string {
  if (!code) return '—';
  if (code === 'reversal') return 'Reversal';
  return ADJUSTMENT_REASON_LABELS[code as AdjustmentReason] ?? code;
}

/**
 * Signed quantity for an adjustment row: a positive (incoming) movement
 * has a to-location set; a negative (outgoing) movement has a from-location
 * set. The trigger applies the same convention to on-hand.
 */
function signedQuantity(row: StockMovementRow): number {
  const qty = row.quantity ?? 0;
  if (row.to_location_id && !row.from_location_id) return qty;
  if (row.from_location_id && !row.to_location_id) return -qty;
  return qty;
}

/**
 * Adjustments ledger — the slice of stock movements that correct on-hand
 * rather than move it: cycle-count corrections and reason-coded
 * adjustments (damage, expiry, shrinkage, found stock, returns). One-off
 * adjustments are posted from here; a full physical count runs on the
 * Cycle count page. Everything lands in the same `stock_movements` audit
 * trail as the rest of inventory.
 */
export function AdjustmentsPage() {
  const navigate = useNavigate();
  const { role, profile, user } = useAuth();
  const { data, isLoading } = useStockMovements({
    types: ['adjustment', 'cycle_count'],
    limit: 500,
  });
  const [adjustmentOpen, setAdjustmentOpen] = useState(false);
  const [editing, setEditing] = useState<StockMovementRow | null>(null);

  const canManage = canWrite(role, 'inventory');
  const countInProgress = useMemo(
    () => hasCycleCountInProgress(profile?.organization_id, user?.id),
    [profile?.organization_id, user?.id],
  );
  const cycleCountLabel = countInProgress ? 'Resume cycle count' : 'Cycle count';
  const openCycleCount = () => navigate('/inventory/cycle-count');
  const rows = useMemo<StockMovementRow[]>(() => data ?? [], [data]);

  const stats = useMemo(() => {
    let increases = 0;
    let decreases = 0;
    let cycleCounts = 0;
    for (const r of rows) {
      const signed = signedQuantity(r);
      if (signed >= 0) increases += signed;
      else decreases += signed;
      if (r.type === 'cycle_count') cycleCounts += 1;
    }
    return {
      total: rows.length,
      cycleCounts,
      increases,
      decreases,
      net: increases + decreases,
    };
  }, [rows]);

  const columns = useMemo<ColumnDef<StockMovementRow>[]>(
    () => [
      {
        id: 'created_at',
        accessorKey: 'created_at',
        header: 'When',
        size: 160,
        minSize: 120,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted-foreground">
            {formatDateTime(row.original.created_at)}
          </span>
        ),
      },
      {
        id: 'type',
        accessorKey: 'type',
        header: 'Mode',
        size: 130,
        minSize: 100,
        filterFn: arrIncludesSome,
        cell: ({ row }) => (
          <Badge variant={MODE_VARIANT[row.original.type] ?? 'secondary'}>
            {STOCK_MOVEMENT_TYPE_LABELS[row.original.type] ?? row.original.type}
          </Badge>
        ),
      },
      {
        id: 'sku',
        accessorFn: (r) => r.items?.sku ?? '',
        header: 'SKU',
        size: 140,
        minSize: 90,
        cell: ({ row }) => (
          <Link
            to={`/catalog/items/${row.original.item_id}?tab=inventory`}
            className="whitespace-nowrap font-mono text-xs text-foreground hover:underline"
          >
            {row.original.items?.sku ?? '—'}
          </Link>
        ),
      },
      {
        id: 'name',
        accessorFn: (r) => r.items?.name ?? '',
        header: 'Item',
        size: 220,
        minSize: 140,
        cell: ({ row }) => (
          <span className="font-medium text-foreground">{row.original.items?.name ?? '—'}</span>
        ),
      },
      {
        id: 'location',
        accessorFn: (r) => r.from?.name ?? r.to?.name ?? '',
        header: 'Location',
        size: 170,
        minSize: 120,
        cell: ({ row }) => {
          const loc = row.original.from ?? row.original.to;
          return loc ? (
            <span>
              {loc.name}
              <span className="ml-2 text-xs text-muted-foreground">({loc.code})</span>
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          );
        },
      },
      {
        id: 'reason',
        accessorFn: (r) => reasonLabel(r.reason_code),
        header: 'Reason',
        size: 150,
        minSize: 110,
        filterFn: arrIncludesSome,
        cell: ({ row }) =>
          row.original.reason_code ? (
            <Badge variant="outline">{reasonLabel(row.original.reason_code)}</Badge>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'quantity',
        accessorFn: (r) => signedQuantity(r),
        header: () => <span className="block text-right">Change</span>,
        size: 110,
        minSize: 90,
        sortingFn: 'basic',
        cell: ({ row }) => {
          const signed = signedQuantity(row.original);
          return (
            <span
              className={
                'block text-right tabular-nums ' +
                (signed > 0
                  ? 'text-bright-moss-dark'
                  : signed < 0
                    ? 'text-fresh-salmon-dark'
                    : 'text-muted-foreground')
              }
            >
              {signed > 0 ? '+' : ''}
              {signed.toLocaleString()}
              <span className="ml-1 text-xs text-muted-foreground">
                {row.original.items?.base_uom}
              </span>
            </span>
          );
        },
      },
      {
        id: 'memo',
        accessorKey: 'memo',
        header: 'Memo',
        size: 220,
        minSize: 120,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.memo ? (
            <span className="text-muted-foreground" title={row.original.memo}>
              {row.original.memo}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
    ],
    [],
  );

  const facetedFilters: FacetedFilter[] = useMemo(
    () => [
      { columnId: 'type', title: 'Mode', options: MODE_OPTIONS },
      { columnId: 'reason', title: 'Reason', options: REASON_OPTIONS },
    ],
    [],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Adjustments"
        actions={
          canManage ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setAdjustmentOpen(true)}>
                <SlidersHorizontal className="h-4 w-4" />
                Adjustment
              </Button>
              <Button onClick={openCycleCount}>
                <ClipboardCheck className="h-4 w-4" />
                {cycleCountLabel}
              </Button>
            </div>
          ) : undefined
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Adjustments"
          value={stats.total.toLocaleString()}
          loading={isLoading}
          hint="Most recent 500"
          icon={<SlidersHorizontal className="h-4 w-4 text-muted-foreground" />}
        />
        <MetricCard
          label="Cycle counts"
          value={stats.cycleCounts.toLocaleString()}
          loading={isLoading}
          icon={<ClipboardCheck className="h-4 w-4 text-muted-foreground" />}
        />
        <MetricCard
          label="Units removed"
          value={Math.abs(stats.decreases).toLocaleString()}
          loading={isLoading}
          icon={<TrendingDown className="h-4 w-4 text-muted-foreground" />}
        />
        <MetricCard
          label="Net unit change"
          value={`${stats.net > 0 ? '+' : ''}${stats.net.toLocaleString()}`}
          loading={isLoading}
          icon={<Scale className="h-4 w-4 text-muted-foreground" />}
        />
      </div>

      <Card>
        <CardContent className="pt-6">
          <DataTable<StockMovementRow, unknown>
            data={rows}
            columns={columns}
            isLoading={isLoading}
            getRowId={(r) => r.id}
            onRowClick={canManage ? setEditing : undefined}
            rowClassName={(r) => (r.status === 'reversed' ? 'opacity-60' : undefined)}
            searchPlaceholder="Search by SKU, item, location, or memo…"
            facetedFilters={facetedFilters}
            initialPageSize={50}
            pageSizes={[25, 50, 100, 200]}
            tableId="inventory-adjustments"
            mobileCard={{
              title: (row) => row.items?.name ?? '—',
              subtitle: (row) => <span className="font-mono text-[11px]">{row.items?.sku}</span>,
              badges: (row) => [
                <Badge key="mode" variant={MODE_VARIANT[row.type] ?? 'secondary'}>
                  {STOCK_MOVEMENT_TYPE_LABELS[row.type] ?? row.type}
                </Badge>,
                row.reason_code ? (
                  <Badge key="reason" variant="outline">
                    {reasonLabel(row.reason_code)}
                  </Badge>
                ) : null,
              ],
              amount: (row) => {
                const signed = signedQuantity(row);
                return {
                  value: `${signed > 0 ? '+' : ''}${signed.toLocaleString()} ${row.items?.base_uom ?? ''}`,
                  tone: signed > 0 ? 'positive' : signed < 0 ? 'negative' : 'neutral',
                };
              },
              meta: (row) => {
                const loc = row.from ?? row.to;
                const m: { label: string; value: React.ReactNode }[] = [
                  { label: 'When', value: formatDateTime(row.created_at) },
                ];
                if (loc) m.push({ label: 'Location', value: `${loc.name} (${loc.code})` });
                return m;
              },
            }}
            emptyState={
              <EmptyState
                icon={SlidersHorizontal}
                title="No adjustments yet"
                action={
                  canManage ? (
                    <Button onClick={openCycleCount}>
                      <ClipboardCheck className="h-4 w-4" />
                      {cycleCountLabel}
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        </CardContent>
      </Card>

      {canManage && (
        <>
          <AdjustmentDialog
            open={adjustmentOpen}
            onOpenChange={setAdjustmentOpen}
            defaultMode="adjustment"
          />
          <EditStockMovementDialog
            open={editing !== null}
            onOpenChange={(o) => !o && setEditing(null)}
            movement={editing}
          />
        </>
      )}
    </div>
  );
}
